/**
 * T2 Business Dashboard — the factory band and the ageing band.
 *
 * Both follow the supplied design reference (Supply Chain Dashboard v5): a heading over a muted
 * sub-line, then bars on a hairline grid rather than inside a card. Colours are the reference's own
 * `--t2d-*` tokens from `index.css`; the only inline style is a runtime-computed bar size, which is
 * §4.8's one exception.
 *
 * Neither panel computes a measure. Shares, SDP and the ageing buckets all arrive from ADB
 * ([DATA-1]); a bar's length against its largest sibling is presentation, and that is all that is
 * worked out here.
 *
 * SDP is marked only when the target is met. The target is 100%, so a "below target" treatment
 * would fire on every real factory and colour the whole panel without telling the reader anything
 * the percentage does not.
 */

import { Bar, BarChart, Cell, LabelList, XAxis, YAxis } from 'recharts';

import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { integer } from '@/components/report/groupedColumns';
import { cn } from '@/lib/utils';

import {
  measureFigure,
  measureLabel,
  meetsTarget,
  orderStateWord,
  pct,
  widthPct,
  type AgeingRow,
  type FactoryRow,
} from './contract';

/** The reference's panel heading and its sub-line. */
function PanelHead({ title, note }: { title: string; note: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <h3 className="text-[15px] font-semibold text-t2d-ink">{title}</h3>
      <p className="text-xs text-t2d-muted">{note}</p>
    </div>
  );
}

/**
 * The reference cycles four hues down the factory list so neighbouring bars stay apart. It is
 * identity, not grading: a factory's colour says which factory, never how it is doing.
 */
const FACTORY_BAR = ['bg-t2d-blue', 'bg-t2d-good', 'bg-t2d-violet', 'bg-t2d-gold'] as const;

/** SDP as the reference draws it: the figure, tinted only when the one global target is met. */
function Sdp({ row }: { row: FactoryRow }) {
  const met = meetsTarget(row.sdp_pct, row.sdp_on_time_qty, row.sdp_delivered_qty);
  if (met === null) {
    return <span className="text-sm text-t2d-faint tabular-nums">—</span>;
  }
  return (
    <span
      className={cn(
        'text-sm font-semibold whitespace-nowrap tabular-nums',
        met ? 'text-t2d-good-ink' : 'text-t2d-ink',
      )}
    >
      {pct(row.sdp_pct, 0)} SDP{met ? ' · at target' : ''}
    </span>
  );
}

export function FactoryBars({
  rows,
  measure,
  orderState,
  complete,
}: {
  rows: readonly FactoryRow[];
  measure: string;
  orderState: string;
  /**
   * The response was not capped. A share is a whole-result claim (§4.3): over a truncated result it
   * would be a share of the rows that loaded, presented as a share of the business.
   */
  complete: boolean;
}) {
  const largest = rows.reduce((max, row) => Math.max(max, row.measure_value ?? 0), 0);
  const title = `${measureLabel(measure)} ${orderStateWord(orderState)} by factory`;

  return (
    <section aria-label={title} className="flex min-w-0 flex-col gap-2 px-6 py-3">
      <PanelHead
        title={title}
        note={complete ? 'bar = share of total · % = SDP' : '% = SDP · shares withheld, result capped'}
      />

      {rows.length === 0 ? (
        <p className="py-6 text-sm text-t2d-muted">
          No factory ordered in this period. Widen the dates, or clear a filter.
        </p>
      ) : (
        <ol className="flex flex-col">
          {rows.map((row, index) => (
            <li
              key={row.factory_group ?? 'unknown'}
              className="flex flex-col gap-1 border-t border-t2d-line-soft py-1.5 first:border-t-0 first:pt-0"
            >
              <div className="flex items-baseline gap-2">
                <span className="w-10 shrink-0 text-[15px] font-semibold text-t2d-ink">
                  {row.factory_group ?? '—'}
                </span>
                <span className="text-[17px] font-semibold text-t2d-ink tabular-nums">
                  {measureFigure(row.measure_value)}
                </span>
                {complete ? (
                  <span className="text-xs text-t2d-faint tabular-nums">
                    {pct(row.share_pct)} of total
                  </span>
                ) : null}
                <span className="flex-1" />
                <Sdp row={row} />
              </div>

              {/* The one inline style §4.8 allows: a runtime-computed width. */}
              <div className="h-2 bg-t2d-track">
                <div
                  className={cn('h-full', FACTORY_BAR[index % FACTORY_BAR.length])}
                  style={{ width: widthPct(row.measure_value, largest) }}
                />
              </div>

              <span className="truncate text-[11.5px] text-t2d-muted tabular-nums">
                {row.supplier_count ?? '—'} suppliers · {row.po_count ?? '—'} orders ·{' '}
                {measureFigure(row.open_qty)} pcs still open
              </span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

/**
 * The reference's four overdue buckets plus one for orders that have not come due yet. The first is
 * deliberately the calm colour — it is not a problem, and colouring it like one would misreport the
 * book.
 *
 * The panel is the NEAR-TERM book: due within 15 days, or already overdue. Orders due further out,
 * and orders with no promised date, are left out in SQL — they swamped everything beside them. So
 * these bars do not sum to the KPI tile's open figure, which is still the whole open book.
 *
 * A CSS variable in an SVG paint attribute, the way `chartColor` reaches Recharts. The RAW token,
 * not the `--color-` alias: `@theme inline` never emits those as custom properties.
 */
const AGE_FILL: Record<number, string> = {
  1: 'var(--t2d-blue)',
  2: 'var(--t2d-good)',
  3: 'var(--t2d-gold)',
  4: 'var(--t2d-warn)',
  5: 'var(--t2d-bad)',
};

interface AgeBar {
  label: string;
  value: number;
  orders: number;
  seq: number;
}

/**
 * Two lines beside each bar — the bucket, then how many orders sit in it. Recharts gives a tick one
 * string, so the count travels on the datum and is looked up by label.
 */
function AgeTick({
  x = 0,
  y = 0,
  payload,
  bars,
}: {
  x?: number;
  y?: number;
  payload?: { value?: unknown };
  bars: readonly AgeBar[];
}) {
  const label = String(payload?.value ?? '');
  const bar = bars.find((b) => b.label === label);
  return (
    <text x={x} y={y} textAnchor="end" className="fill-t2d-ink">
      <tspan x={x} dy={-2} className="text-[11.5px] font-medium">
        {label}
      </tspan>
      <tspan x={x} dy={13} className="fill-t2d-faint text-[11px] tabular-nums">
        {bar === undefined ? '' : `${integer.format(bar.orders)} orders`}
      </tspan>
    </text>
  );
}

export function AgeingBars({ rows, measure }: { rows: readonly AgeingRow[]; measure: string }) {
  const nothingOpen = rows.every((row) => (row.measure_value ?? 0) === 0);
  const title = 'Ageing of open orders';
  const bars: AgeBar[] = rows.map((row) => ({
    label: row.bucket_label ?? '—',
    value: Math.max(0, row.measure_value ?? 0),
    orders: row.po_count ?? 0,
    seq: row.bucket_seq ?? 0,
  }));
  const config: ChartConfig = { value: { label: measureLabel(measure) } };

  return (
    <section aria-label={title} className="flex h-full min-w-0 flex-col gap-2 px-6 py-3">
      <PanelHead
        title={title}
        note={`days past due date · due within 15 days or overdue · ${measureLabel(measure).toLowerCase()}`}
      />

      {nothingOpen ? (
        <p className="py-6 text-sm text-t2d-muted">
          Nothing is due in the next 15 days and nothing is overdue. Orders due further out are not
          counted here.
        </p>
      ) : (
        <ChartContainer
            config={config}
            aria-label={title}
            className="aspect-auto h-full min-h-52 w-full flex-1"
          >
          {/* Horizontal bars: one bucket routinely carries most of the open book, so upright bars
              left four of five as slivers under a mostly empty panel. Laid on their side the
              category labels get real room and the bars fill the width. */}
          <BarChart
            data={bars}
            layout="vertical"
            margin={{ top: 4, right: 56, bottom: 4, left: 4 }}
            accessibilityLayer
          >
            <XAxis type="number" hide domain={[0, 'dataMax']} />
            <YAxis
              type="category"
              dataKey="label"
              tickLine={false}
              axisLine={false}
              interval={0}
              width={92}
              tick={<AgeTick bars={bars} />}
            />
            <ChartTooltip
              cursor={{ fill: 'var(--t2d-track)' }}
              content={
                <ChartTooltipContent
                  formatter={(value, _name, item) => {
                    const datum = (item as { payload?: AgeBar } | undefined)?.payload;
                    return (
                      <span className="flex flex-col gap-0.5">
                        <span className="font-medium tabular-nums">
                          {integer.format(Number(value))} {measure === 'value' ? 'HKD' : 'pcs'}
                        </span>
                        <span className="text-muted-foreground tabular-nums">
                          {integer.format(datum?.orders ?? 0)} orders still open
                        </span>
                      </span>
                    );
                  }}
                />
              }
            />
            <Bar dataKey="value" isAnimationActive={false} maxBarSize={44}>
              <LabelList
                dataKey="value"
                position="right"
                offset={8}
                className="fill-t2d-ink text-[13px] font-semibold"
                formatter={(v: unknown) => measureFigure(Number(v))}
              />
              {bars.map((bar) => (
                <Cell key={bar.label} fill={AGE_FILL[bar.seq] ?? 'var(--t2d-ink)'} />
              ))}
            </Bar>
          </BarChart>
        </ChartContainer>
      )}
    </section>
  );
}
